import { useState, useEffect } from "react";

export interface PixelfedPost {
  title: string;
  postUrl: string;
  imageUrl: string | null;
  date: string;
}

const ATOM_FEED_URL = "https://pixl.fi/users/elias.atom";
const CORS_PROXY = "https://api.codetabs.com/v1/proxy/?quest=";

export function usePixelfedPost() {
  const [post, setPost] = useState<PixelfedPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(`${CORS_PROXY}${encodeURIComponent(ATOM_FEED_URL)}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((xml) => {
        if (cancelled) return;
        const parser = new DOMParser();
        const doc = parser.parseFromString(xml, "application/xml");

        const parserError = doc.querySelector("parsererror");
        if (parserError) throw new Error("Failed to parse XML");

        const entry = doc.querySelector("entry");
        if (!entry) throw new Error("No entries in feed");

        const title =
          entry.querySelector("title")?.textContent?.trim() ?? "Pixelfed post";
        const updated = entry.querySelector("updated")?.textContent ?? "";
        const linkEl = entry.querySelector('link[rel="alternate"]');
        const postUrl =
          linkEl?.getAttribute("href") ?? "https://pixl.fi/elias";

        // Try <media:content url="..."> first
        const mediaContent = entry.querySelector("content[url]");
        let imageUrl: string | null =
          mediaContent?.getAttribute("url") ?? null;

        // Fallback: parse <content type="html"> and extract first <img src>
        if (!imageUrl) {
          const contentHtml =
            entry.querySelector("content")?.textContent ?? "";
          const imgMatch = contentHtml.match(/<img[^>]+src="([^"]+)"/);
          imageUrl = imgMatch ? imgMatch[1] : null;
        }

        // media namespace fallback
        if (!imageUrl) {
          // Try getElementsByTagNameNS
          const mediaNS = "http://search.yahoo.com/mrss/";
          const mediaEls = entry.getElementsByTagNameNS(mediaNS, "content");
          if (mediaEls.length > 0) {
            imageUrl = mediaEls[0].getAttribute("url");
          }
        }

        setPost({ title, postUrl, imageUrl, date: updated });
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { post, loading, error };
}
